import { ref } from 'vue'
import { consola } from 'consola'
import { useInstitutionStore } from '@entities/institution'
import { useUserProfileStore } from '@entities/user'
import { useMaxWebApp } from '@shared/lib'
import { getClientFingerprint } from '@shared/lib/auth'
import { useRuntimeConfig } from '#imports'
import apiAuth, { useSessionManager } from '~/entities/auth'
import { useLogoutHandler } from '~/features/auth/model'
import { useAuthAnalytics } from '~/features/auth/model/useAuthAnalytics'

const logger = consola.withTag('max-webapp-auth')

/**
 * Авторизация при запуске сайта как мини-приложения MAX (WebApp).
 *
 * Поток:
 *   1. Проверяем, что мы внутри MAX и сессии ещё нет.
 *   2. Берём подписанный initData из Bridge и шлём на бэк.
 *   3. Если бэк просит контакт — вызываем requestContact() и повторяем запрос.
 *   4. Сохраняем токены тем же путём, что обычный логин, и грузим профиль.
 *
 * Токены кладутся в localStorage (useSessionManager), поэтому номер телефона
 * запрашивается только на первый вход на устройстве.
 */
export const useMaxWebAppAuth = () => {
  const max = useMaxWebApp()
  const storeInstitution = useInstitutionStore()
  const sessionManager = useSessionManager()
  const userProfileStore = useUserProfileStore()
  const logoutHandler = useLogoutHandler()
  const authAnalytics = useAuthAnalytics()

  const isAuthorizing = ref(false)

  const resolveInstitutionId = (): string | null => {
    const config = useRuntimeConfig()
    // currentId — мёртвый ref (всегда 1, см. useInstitutionStore): источник истины
    // это загруженное заведение, затем env-конфиг деплоя. Прежний `?? config` не
    // срабатывал, т.к. currentId никогда не nullish.
    const id = (storeInstitution.institution as any)?.id ?? config.public.institutionID
    return id != null && String(id).length > 0 ? String(id) : null
  }

  /**
   * Пытается авторизоваться через MAX. Возвращает true при успешном входе.
   * Безопасна вне MAX и при уже активной сессии — просто вернёт false.
   */
  const tryAutoLogin = async (): Promise<boolean> => {
    if (!process.client || !max.isMaxEnv()) return false
    if (sessionManager.hasActiveSession.value) return false
    if (isAuthorizing.value) return false

    const institutionId = resolveInstitutionId()
    if (!institutionId) {
      logger.warn('Не удалось определить institutionId для MAX-входа')
      return false
    }

    isAuthorizing.value = true
    try {
      const bridge = await max.ensureBridge()
      const initData = bridge?.initData
      if (!initData) {
        logger.warn('initData недоступен — вход через MAX пропущен')
        return false
      }

      // Отпечаток устройства: на нём держится порог выдач приветственных баллов
      // на одно устройство. Считается один раз за сессию и кэшируется.
      const clientFingerprint = await getClientFingerprint()

      // 1. Первый запрос без контакта.
      let { data } = await apiAuth.maxWebAppAuth({ institutionId, initData, clientFingerprint })

      // 2. Бэк просит телефон — запрашиваем контакт и повторяем.
      if (data.requiresContact) {
        const contact = await max.requestContact()
        if (!contact?.phone) {
          logger.warn('Пользователь не поделился контактом — вход не завершён')
          return false
        }
        ;({ data } = await apiAuth.maxWebAppAuth({
          institutionId,
          initData,
          contact: { phone: contact.phone, authDate: contact.authDate, hash: contact.hash },
          clientFingerprint,
        }))
      }

      if (!data.accessToken || !data.sessionId) {
        logger.warn('Бэк не вернул токены MAX-авторизации')
        return false
      }

      // Автовход внутри мини-приложения MAX: кнопки не было, поэтому попытку и её
      // исход отмечаем здесь — иначе успех не с чем связать и в воронку он не попадёт.
      authAnalytics.trackMethodClick('max', 'max_webapp')
      authAnalytics.trackCallback('max', true)

      // 3. Сохраняем сессию тем же путём, что обычный логин.
      sessionManager.setSession(data.accessToken, data.sessionId, data.refreshToken)
      logoutHandler.handleSuccessfulLogin()
      await userProfileStore.fetchUser()

      // 4. Те же побочные эффекты, что у штатного входа (не критичны).
      try {
        const { useApplyReferralToken } = await import('~/features/profile/@x/auth')
        await useApplyReferralToken()
      } catch (error) {
        logger.warn('Реферальный токен не применён (не критично):', error)
      }
      try {
        const pushSettingsModule = await import('~/features/notifications/@x/auth')
        await pushSettingsModule.usePushNotificationSettings().initPushNotificationsAfterAuth()
      } catch (error) {
        logger.warn('Push notifications не инициализированы (не критично):', error)
      }

      max.hapticNotify('success')
      logger.success('Вход через мини-приложение MAX выполнен')
      return true
    } catch (error: any) {
      logger.error('Ошибка входа через MAX:', error?.data?.message || error?.message || error)
      return false
    } finally {
      isAuthorizing.value = false
    }
  }

  return { tryAutoLogin, isAuthorizing }
}
